// Formatting helpers for the Scheduler UI. All user-facing time strings are
// relative ("in 25 min", "3 min ago") with the raw timestamp on hover.

export const JOB_TYPES = {
  customers: {
    label: 'Customer Sync',
    plural: 'Customers',
    direction: 'Pull · Epicor → Middleware',
    description: 'Customer master records from Epicor (includes ship-to addresses).',
  },
  parts: {
    label: 'Part Sync',
    plural: 'Parts',
    direction: 'Pull · Epicor → Middleware',
    description: 'Part master records: descriptions, pricing fields, web flags.',
  },
  products: {
    label: 'Product Sync (Epicor → PIM)',
    plural: 'Products (PIM)',
    direction: 'Pull · Epicor → PIM',
    description: 'Part master into the PIM: product_details core fields, then PIM catalog backfill and projection refresh.',
  },
  pricing: {
    label: 'Pricing Sync',
    plural: 'Pricing',
    direction: 'Pull · Epicor → Middleware',
    description: 'All 9 price-list and assignment tables, refreshed atomically.',
  },
  shipto: {
    label: 'Ship-to Address Sync',
    plural: 'Ship-to Addresses',
    direction: 'Pull · Epicor → Middleware',
    description: 'Ship-to delivery addresses only, without the full customer sync.',
  },
  inventory: {
    label: 'Inventory Sync',
    plural: 'Inventory',
    direction: 'Pull · Epicor → Middleware',
    description: 'Per-SKU, per-warehouse stock buckets from Erp.PartWhse. Incremental runs pull changes only; schedule a periodic full run to reconcile removals.',
  },
  bc_products: {
    label: 'BC Product Push (PIM → BigCommerce)',
    plural: 'BC Product Push',
    direction: 'Push · PIM → BigCommerce',
    push: true,
    description: 'Creates and updates BigCommerce products from the PIM: fields, channel and category assignments, and images. Diff-driven — only changed products are touched.',
  },
  bc_pricing: {
    label: 'BC Price List Push',
    plural: 'BC Price Lists',
    direction: 'Push · Middleware → BigCommerce',
    push: true,
    description: 'Per-customer price lists and customer-group assignments. Hard-gated on the order audit (a blocked gate fails the run). Note: product pushes that create products auto-heal price lists too, so runs may overlap harmlessly.',
  },
  bc_customers: {
    label: 'BC Customer Push',
    plural: 'BC Customer Push',
    direction: 'Push · Middleware → BigCommerce',
    push: true,
    description: 'Companies (customers) from the middleware staging tables into BigCommerce B2B.',
  },
  bc_addresses: {
    label: 'BC Address Push',
    plural: 'BC Address Push',
    direction: 'Push · Middleware → BigCommerce',
    push: true,
    description: 'Company addresses from the middleware staging tables into BigCommerce B2B.',
  },
};

export const isPushJobType = (jobType) => Boolean(JOB_TYPES[jobType]?.push);

// Selectable push targets: one job is enqueued per selected pair on each fire.
export const TARGET_OPTIONS = [
  { market: 'AU', environment: 'prod', label: 'Australia · Production' },
  { market: 'NZ', environment: 'prod', label: 'New Zealand · Production' },
  { market: 'AU', environment: 'staging', label: 'Australia · Staging' },
  { market: 'NZ', environment: 'staging', label: 'New Zealand · Staging' },
];

export const targetKey = (t) => `${t.market}:${t.environment}`;

export function targetChipLabel(t) {
  return `${t.market} ${t.environment === 'staging' ? 'Staging' : 'Prod'}`;
}

export const TIMEZONES = [
  'Australia/Sydney',
  'Australia/Brisbane',
  'Australia/Melbourne',
  'Australia/Perth',
  'Pacific/Auckland',
  'UTC',
];

export const STATUS_LABELS = {
  pending: 'Queued',
  running: 'Running',
  succeeded: 'Succeeded',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

export function cadenceText(schedule) {
  if (schedule.schedule_type === 'interval') {
    const m = schedule.interval_minutes || 0;
    if (m % 1440 === 0 && m >= 1440) {
      const d = m / 1440;
      return `Every ${d === 1 ? 'day' : `${d} days`}`;
    }
    if (m % 60 === 0 && m >= 60) {
      const h = m / 60;
      return `Every ${h === 1 ? 'hour' : `${h} hours`}`;
    }
    return `Every ${m} min`;
  }
  const times = (schedule.run_times || []).join(', ');
  const tz = schedule.timezone || 'Australia/Sydney';
  const tzShort = tz.split('/').pop().replace('_', ' ');
  return `Daily at ${times} (${tzShort})`;
}

export function relativeTime(iso, nowMs = Date.now()) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  let diff = Math.round((t - nowMs) / 1000);
  const future = diff > 0;
  diff = Math.abs(diff);

  let text;
  if (diff < 45) text = future ? 'in under a minute' : 'just now';
  else if (diff < 3600) {
    const m = Math.round(diff / 60);
    text = future ? `in ${m} min` : `${m} min ago`;
  } else if (diff < 86400) {
    const h = Math.floor(diff / 3600);
    const m = Math.round((diff % 3600) / 60);
    const span = m > 0 ? `${h} h ${m} m` : `${h} h`;
    text = future ? `in ${span}` : `${span} ago`;
  } else {
    const d = Math.round(diff / 86400);
    text = future ? `in ${d} day${d === 1 ? '' : 's'}` : `${d} day${d === 1 ? '' : 's'} ago`;
  }
  return text;
}

export function absoluteTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    weekday: 'short', day: 'numeric', month: 'short',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

export function durationText(startIso, endIso) {
  if (!startIso || !endIso) return '—';
  const ms = new Date(endIso).getTime() - new Date(startIso).getTime();
  if (Number.isNaN(ms) || ms < 0) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
}

// Added/updated split for a run, derived from job counters. Returns
// { added, updated } or null when the counters predate the split (old runs)
// or are absent. "updated" means "existing row refreshed by the run" for all
// job types except inventory, where it means "stock values actually changed".
export function lastRunAddedUpdated(jobType, counters) {
  if (!counters || typeof counters !== 'object') return null;
  if (jobType === 'customers') {
    if (counters.customers_added == null && counters.shiptos_added == null) return null;
    return {
      added: (counters.customers_added || 0) + (counters.shiptos_added || 0),
      updated: (counters.customers_updated || 0) + (counters.shiptos_updated || 0),
    };
  }
  if (jobType === 'bc_products') {
    if (counters.created == null && counters.updated == null) return null;
    return { added: counters.created || 0, updated: counters.updated || 0 };
  }
  if (jobType === 'bc_pricing') {
    if (counters.price_records_upserted == null) return null;
    return { added: 0, updated: counters.price_records_upserted || 0 };
  }
  if (jobType === 'bc_customers') {
    if (counters.companies_created == null && counters.companies_updated == null) return null;
    return { added: counters.companies_created || 0, updated: counters.companies_updated || 0 };
  }
  if (jobType === 'bc_addresses') {
    if (counters.addresses_created == null && counters.addresses_updated == null) return null;
    return { added: counters.addresses_created || 0, updated: counters.addresses_updated || 0 };
  }
  if (counters.added == null && counters.updated == null) return null;
  return { added: counters.added || 0, updated: counters.updated || 0 };
}

// Total rows processed for a finished run, derived from job counters.
export function rowsProcessed(counters) {
  if (!counters || typeof counters !== 'object') return null;
  if (counters.customers_upserted != null || counters.shiptos_upserted != null) {
    return (counters.customers_upserted || 0) + (counters.shiptos_upserted || 0);
  }
  if (counters.upserted != null) return counters.upserted;
  if (counters.fetched != null) return counters.fetched;
  return null;
}

// Client-side preview of the next fire time, mirroring the backend logic
// closely enough for the editor's live summary.
export function previewNextRun({ scheduleType, intervalMinutes, runTimes, timezone }) {
  const now = new Date();
  if (scheduleType === 'interval') {
    if (!intervalMinutes || intervalMinutes < 1) return null;
    return new Date(now.getTime() + intervalMinutes * 60000);
  }
  const valid = (runTimes || []).filter((t) => /^([01]\d|2[0-3]):[0-5]\d$/.test(t));
  if (!valid.length) return null;
  // Interpret HH:MM in the schedule's timezone via the Intl API.
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone, hour: '2-digit', minute: '2-digit', hour12: false,
  });
  const nowLocal = fmt.format(now); // "HH:MM" in schedule tz
  const sorted = [...valid].sort();
  const todayNext = sorted.find((t) => t > nowLocal);
  // Approximate: difference between target HH:MM and current HH:MM in the tz.
  const [nh, nm] = nowLocal.split(':').map(Number);
  const target = todayNext || sorted[0];
  const [th, tm] = target.split(':').map(Number);
  let minutes = (th * 60 + tm) - (nh * 60 + nm);
  if (!todayNext) minutes += 1440;
  return new Date(now.getTime() + minutes * 60000);
}

// Job types the backend always runs as a full refresh — they must not show
// a "Full" chip as if it were a configurable mode. Mirrors the model
// validator's ("pricing", "bc_products", "bc_pricing") rule.
const ALWAYS_FULL_JOB_TYPES = new Set(['pricing', 'bc_products', 'bc_pricing']);
export function isAlwaysFull(jobType) {
  return ALWAYS_FULL_JOB_TYPES.has(jobType);
}

// Streams group schedules by the data they move. Every one of the ten job
// types is covered; a type missing here falls into OTHER_STREAM, so a new
// backend job type is never silently hidden from the overview.
export const OTHER_STREAM = {
  id: 'other',
  label: 'Other jobs',
  from: '',
  to: '',
  kind: 'pull',
  note: 'Jobs not yet mapped to a stream.',
};
export const STREAMS = [
  {
    id: 'customers', label: 'Customers', from: 'Epicor', to: 'Middleware', kind: 'pull',
    note: 'Accounts, contacts, credit terms and delivery addresses. Nothing reaches a storefront from here.',
    jobTypes: ['customers', 'shipto'],
  },
  {
    id: 'parts', label: 'Parts', from: 'Epicor', to: 'Middleware', kind: 'pull',
    note: 'Part master records: codes, units of measure and warehouse flags.',
    jobTypes: ['parts'],
  },
  {
    id: 'products', label: 'Products', from: 'Epicor', to: 'PIM', kind: 'pull',
    note: 'Feeds the PIM product model — the source for names, copy and taxonomy work.',
    jobTypes: ['products'],
  },
  {
    id: 'pricing', label: 'Pricing', from: 'Epicor', to: 'Middleware', kind: 'pull',
    note: 'Price lists per company. List price stays owned by Epicor — the console never edits it.',
    jobTypes: ['pricing'],
  },
  {
    id: 'inventory', label: 'Inventory', from: 'Epicor', to: 'Middleware', kind: 'pull',
    note: 'Warehouse stock on hand. The fastest cadence in the console.',
    jobTypes: ['inventory'],
  },
  {
    id: 'storefront', label: 'Storefront publish', from: 'Middleware', to: 'BigCommerce', kind: 'push',
    note: 'The only jobs that write to a live storefront. Everything above only stages data.',
    jobTypes: ['bc_products', 'bc_pricing', 'bc_customers', 'bc_addresses'],
  },
];
const JOB_TYPE_TO_STREAM = STREAMS.reduce((acc, stream) => {
  stream.jobTypes.forEach((t) => { acc[t] = stream; });
  return acc;
}, {});
export function streamForJobType(jobType) {
  return JOB_TYPE_TO_STREAM[jobType] || OTHER_STREAM;
}

// Break a persisted schedule into the inline editor's cadence controls:
// kind ('mins' | 'hours' | 'daily') plus the value shown beside it. When a
// daily schedule has several run times, return multi so the row links to
// the full editor instead of a single-time field that would drop the rest.
export function cadenceParts(schedule) {
  if (!schedule) return { kind: 'mins', value: '30' };
  if (schedule.schedule_type === 'daily') {
    const times = Array.isArray(schedule.run_times) ? schedule.run_times : [];
    if (times.length > 1) return { kind: 'daily', value: null, multi: true };
    return { kind: 'daily', value: times[0] || '02:00' };
  }
  const m = schedule.interval_minutes || 0;
  if (m >= 60 && m % 60 === 0) return { kind: 'hours', value: String(m / 60) };
  return { kind: 'mins', value: String(m || 30) };
}

// Build the full ScheduleBody the PUT endpoint requires, preserving every
// field the inline cadence editor does not touch. Omitting enabled would
// hit its False default and silently pause the schedule while nulling
// next_run_at, so it (and name / job_type / sync_mode / timezone /
// companies / targets) must be carried over unchanged.
export function buildSchedulePayload(schedule, { kind, value }) {
  const push = isPushJobType(schedule.job_type);
  const payload = {
    name: schedule.name,
    enabled: Boolean(schedule.enabled),
    job_type: schedule.job_type,
    sync_mode: isAlwaysFull(schedule.job_type) ? 'full' : (schedule.sync_mode || 'full'),
    timezone: schedule.timezone || 'Australia/Sydney',
    companies: push ? [] : (schedule.companies || ['AU', 'NZ']),
    targets: push
      ? (schedule.targets || []).map((t) => ({ market: t.market, environment: t.environment }))
      : [],
  };
  if (kind === 'daily') {
    payload.schedule_type = 'daily';
    payload.run_times = [value || '02:00'];
  } else {
    payload.schedule_type = 'interval';
    payload.interval_minutes = kind === 'hours'
      ? Math.max(1, parseInt(value, 10) || 1) * 60
      : Math.max(1, parseInt(value, 10) || 1);
  }
  return payload;
}
